import React, { useState, useRef, useEffect } from 'react';
import { Tooltip } from 'antd';
import { useTooltipMode } from 'redux/hooks';

interface ConditionalTooltipProps {
    title: string;
    children: React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
}

export function ConditionalTooltip(props: ConditionalTooltipProps) {
    const tooltipMode = useTooltipMode();
    const { title, children, placement } = props;
    const [showTooltip, setShowTooltip] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (tooltipMode === 'small') {
            timeoutRef.current = setTimeout(() => {
                setShowTooltip(true);
                setTooltipPosition({ x: e.clientX, y: e.clientY });
            }, 2000);
        }
    };

    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setShowTooltip(false);
        setTooltipPosition(null);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (tooltipMode === 'small' && showTooltip) {
            setTooltipPosition({ x: e.clientX, y: e.clientY });
        }
    };

    if (tooltipMode === 'none') {
        return <>{children}</>;
    }

    if (tooltipMode === 'small') {
        // For small mode, create a custom tooltip that appears after 2 seconds
        return (
            <>
                {React.isValidElement(children) ? (
                    React.cloneElement(children as React.ReactElement<any>, {
                        onMouseEnter: (e: React.MouseEvent) => {
                            handleMouseEnter(e);
                            const originalOnMouseEnter = (children as React.ReactElement<any>).props.onMouseEnter;
                            if (originalOnMouseEnter) originalOnMouseEnter(e);
                        },
                        onMouseLeave: (e: React.MouseEvent) => {
                            handleMouseLeave();
                            const originalOnMouseLeave = (children as React.ReactElement<any>).props.onMouseLeave;
                            if (originalOnMouseLeave) originalOnMouseLeave(e);
                        },
                        onMouseMove: (e: React.MouseEvent) => {
                            handleMouseMove(e);
                            const originalOnMouseMove = (children as React.ReactElement<any>).props.onMouseMove;
                            if (originalOnMouseMove) originalOnMouseMove(e);
                        }
                    })
                ) : (
                    <span
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onMouseMove={handleMouseMove}
                    >
                        {children}
                    </span>
                )}
                {showTooltip && tooltipPosition && (
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            left: `${tooltipPosition.x + 10}px`,
                            top: `${tooltipPosition.y - 10}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                        zIndex: 10000,
                        pointerEvents: 'none',
                        maxWidth: '300px',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap'
                        }}
                    >
                        {title}
                    </div>
                )}
            </>
        );
    }

    // For large mode, use Ant Design Tooltip with 2 second delay
    return (
        <Tooltip title={title} placement={placement} trigger="hover" mouseEnterDelay={2}>
            {children}
        </Tooltip>
    );
}

